import {get, includes} from 'lodash';
import {action, observable, toJS, makeObservable, computed} from 'mobx';
import * as Sentry from '@sentry/react';

class ReportIssueStore {
  @observable translations = {};
  @observable notification = null;
  @observable loading = false;
  @observable
  isUploading = false;
  @observable
  formMediaUrl = null;
  @observable
  formFeedbackText = null;

  @observable
  isTryAgainErrorDialogOpen = false;

  @observable
  isRemoveImageDialogOpen = false;

  @observable
  isSaveWithoutNewImageDialogOpen = false;

  @observable
  mediaUploadErrorText = '';

  @observable mediaUpdated = false;
  @observable textUpdated = false;

  @action
  setNotification = notification => (this.notification = notification);

  @action
  setTranslations = translations => (this.translations = translations);

  @action
  setLoading = loading => (this.loading = loading);

  @computed
  get feedbackText() {
    return this.textUpdated
      ? this.formFeedbackText
      : this.store?.checklist?.feedbackForStep(this.store.instructionPage.activeStepId) || '';
  }

  @computed
  get mediaUrl() {
    return this.mediaUpdated
      ? this.formMediaUrl
      : this.store?.checklist?.mediaUrlForStep(this.store.instructionPage.activeStepId);
  }

  @action
  reset = () => {
    this.isUploading = false;
    this.formMediaUrl = null;
    this.formFeedbackText = null;
    this.mediaUpdated = false;
    this.textUpdated = false;
    this.isSaveWithoutNewImageDialogOpen = false;
    this.isRemoveImageDialogOpen = false;
    this.isTryAgainErrorDialogOpen = false;
    this.isUploading = false;
  };

  constructor() {
    makeObservable(this);
  }

  getCheckValue(check) {
    if (!check) return null;
    if (check.checked) return true;
    if (check.unchecked) return false;
    return null;
  }

  @action
  escalateCheck = async escalateCheckMutation => {
    this.setLoading(true);
    const {
      instructionPage: {currentGuideVersionId, activeStepId, currentInstruction},
      checklist: {checklist, updateChecklist}
    } = this.store;
    const checkInfo = checklist[activeStepId];
    const optionId = checkInfo?.optionId;
    const checklistOptions = currentInstruction?.checklistTemplate?.options;
    const templateOption = checklistOptions?.find(option => option.id === optionId);

    const currentChecklist = toJS(checklist);
    const foundCheck = currentChecklist[activeStepId];
    const linkToCheck = window.location.href;
    const checklistName = currentInstruction.title;
    const checklistAnswerItem = {
      stepId: activeStepId,
      value: this.getCheckValue(foundCheck),
      feedback: this.feedbackText || '',
      mediaUrl: this.mediaUrl,
      guideVersionId: currentGuideVersionId,
      templateOption: templateOption
        ? {id: templateOption?.id, value: templateOption?.value, option: templateOption?.option}
        : null,
      linkToCheck,
      checklistName
    };

    try {
      const result = await escalateCheckMutation({checklistAnswerItem});
      if (!get(result, 'data.escalateCheck.result')) {
        const errorMessage = result.errors[0].message;
        const checkEscalationErrors = [
          'step_id_not_provided',
          'guide_version_id_not_provided',
          'webhook_url_not_configured',
          'webhook_url_not_valid',
          'can_not_escalate'
        ];
        const webhookUrlErrors = ['webhook_url_unreachable'];
        if (includes(checkEscalationErrors, errorMessage)) {
          this.notification.error(this.translations.somethingWentWrongPa);
          throw new Error('Check can not be escalated. Contact your platform admin');
        } else if (includes(webhookUrlErrors, errorMessage)) {
          this.notification.error(this.translations.somethingWentWrong);
          throw new Error('Webhook is unreachable. Contact your platform admin');
        } else {
          this.notification.error(this.translations.somethingWentWrong);
          throw new Error('Check can not be escalated');
        }
      }

      this.notification.success(this.translations.reportedCheck);
      updateChecklist(activeStepId, {
        escalationAttemptsIds: [result.data.escalateCheck.checkEscalationAttemptId],
        checkEscalated: true,
        feedbackText: this.feedbackText,
        mediaUrl: this.mediaUrl
      });
      this.reset();

      this.setLoading(false);
      return true;
    } catch (error) {
      Sentry.captureException(error);
      this.setLoading(false);
      return false;
    }
  };

  @action
  setIsUploading = isUploading => {
    this.isUploading = isUploading;
  };

  @action
  setMediaUrl = mediaUrl => {
    this.formMediaUrl = mediaUrl;
    this.mediaUpdated = true;
  };

  @action
  setFeedbackText = feedbackText => {
    this.formFeedbackText = feedbackText;
    this.textUpdated = true;
  };

  @action
  setIsTryAgainErrorDialogOpen = isOpen => {
    this.isTryAgainErrorDialogOpen = isOpen;
  };

  @action
  setMediaUploadErrorText = errorText => {
    this.mediaUploadErrorText = errorText;
  };

  @action
  setIsRemoveImageDialogOpen = isOpen => {
    this.isRemoveImageDialogOpen = isOpen;
  };

  setStore = store => {
    this.store = store;
  };

  @action
  setIsSaveWithoutNewImageDialogOpen = isOpen => {
    this.isSaveWithoutNewImageDialogOpen = isOpen;
  };
}
export default ReportIssueStore;
